import { Copy, Edit, Mail, Phone, Star, Trash2 } from 'lucide-react';
import type { ReactElement } from 'react';
import type { AccountContact, AccountContactItem } from '@/api/commercial/accounts';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface ContactCardSelectableProps {
  contact: AccountContact;
  isSelected: boolean;
  onSelectionChange: (selected: boolean) => void;
  onCopy?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  showEditActions?: boolean;
}

const getContactIcons = (
  type: AccountContactItem['type'],
  preferred: AccountContactItem['preferredContact'] | undefined,
) => {
  const icons: ReactElement[] = [];
  const isWhatsapp = !!preferred?.whatsapp;
  const isPhone = !!preferred?.phone;
  const isEmail = !!preferred?.email;

  if (type === 'Celular') {
    if (isWhatsapp) {
      icons.push(
        <svg
          key="whatsapp"
          className={cn('h-4 w-4', isWhatsapp ? 'text-primary' : 'text-muted-foreground')}
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <title>WhatsApp</title>
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>,
      );
    }
    if (isPhone) {
      icons.push(
        <Phone
          key="phone"
          className={cn('h-4 w-4', isPhone ? 'text-primary' : 'text-muted-foreground')}
        />,
      );
    }
    if (!isWhatsapp && !isPhone) {
      icons.push(<Phone key="phone-default" className="h-4 w-4 text-muted-foreground" />);
    }
  } else if (type === 'Telefone Residencial' || type === 'Telefone Comercial') {
    icons.push(
      <Phone
        key="phone"
        className={cn('h-4 w-4', isPhone ? 'text-primary' : 'text-muted-foreground')}
      />,
    );
  } else if (type === 'Email') {
    icons.push(
      <Mail
        key="email"
        className={cn('h-4 w-4', isEmail ? 'text-primary' : 'text-muted-foreground')}
      />,
    );
  } else {
    icons.push(<Phone key="phone-fallback" className="h-4 w-4 text-muted-foreground" />);
  }

  return icons;
};

const formatContactValue = (type: AccountContactItem['type'], value: string) => {
  if (type === 'Celular' || type === 'Telefone Residencial' || type === 'Telefone Comercial') {
    return value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  return value;
};

export function ContactCardSelectable({
  contact,
  isSelected,
  onSelectionChange,
  onCopy,
  onEdit,
  onDelete,
  showEditActions = false,
}: ContactCardSelectableProps) {
  const { toast } = useToast();
  const contactName = contact?.name?.trim() || 'Contato sem nome';
  const initials = contactName
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const handleCopy = () => {
    const contactInfo =
      `${contactName}\n` +
      (contact.contactItems || []).map((item) => `${item.type}: ${item.contact}`).join('\n');

    if (onCopy) {
      onCopy();
    } else {
      navigator.clipboard.writeText(contactInfo);
      toast({
        title: 'Copiado!',
        description: 'Contato copiado para a area de transferencia.',
      });
    }
  };

  return (
    <Card
      className={cn(
        'w-full max-w-xs flex flex-col mx-0 gap-1 p-4 rounded-lg border bg-card transition-colors cursor-pointer',
        isSelected ? 'border-primary bg-primary/5 hover:bg-primary/10' : 'hover:bg-accent/50',
      )}
      onClick={() => onSelectionChange(!isSelected)}
    >
      <div className="flex gap-3">
        <div className="flex flex-col items-center gap-2">
          <Avatar className="h-12 w-12 border">
            <AvatarImage src="/placeholder.svg" alt={`Foto de ${contact.name}`} />
            <AvatarFallback
              title={contact.name}
              className="bg-primary/10 text-primary font-semibold"
            >
              {initials}
            </AvatarFallback>
          </Avatar>

          <Checkbox
            checked={isSelected}
            onCheckedChange={(checked) => onSelectionChange(!!checked)}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Selecionar contato ${contactName}`}
          />
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 title={contact.name} className="font-semibold text-foreground truncate">
                {contact.name}
              </h3>
              {contact.positionOrRelation && (
                <p className="text-sm text-muted-foreground truncate">
                  {contact.positionOrRelation}
                </p>
              )}
            </div>

            <div className="flex gap-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      className="h-8 w-8 text-muted-foreground hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopy();
                      }}
                      aria-label="Copiar Contato"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p>Copiar contato</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {showEditActions && onEdit && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit();
                        }}
                        aria-label="Editar Contato"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>Editar contato</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {showEditActions && onDelete && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete();
                        }}
                        aria-label="Excluir Contato"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p>Excluir contato</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            {contact.contractEnabled && (
              <Badge variant="outline" className="text-xs">
                Contrato
              </Badge>
            )}
            {contact.contactFor.map((forItem) => (
              <Badge key={forItem} variant="outline" className="text-xs">
                {forItem}
              </Badge>
            ))}
          </div>

          <div className="space-y-1">
            {contact.contactItems.map((item) => {
              const isPreferred =
                (item.type === 'Celular' &&
                  (item.preferredContact.whatsapp || item.preferredContact.phone)) ||
                ((item.type === 'Telefone Residencial' || item.type === 'Telefone Comercial') &&
                  item.preferredContact.phone) ||
                (item.type === 'Email' && item.preferredContact.email);

              return (
                <div key={item.id} className="flex items-center gap-2 text-sm my-auto">
                  <div className="flex items-center gap-1">
                    {getContactIcons(item.type, item.preferredContact)}
                    {isPreferred && <Star className="h-3 w-3 text-primary fill-current" />}
                  </div>
                  <span
                    className={cn(
                      'min-w-0 break-words whitespace-normal',
                      isPreferred ? 'font-medium text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {formatContactValue(item.type, item.contact)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
}
